import React, { useState, useEffect } from 'react';
import ChordDiagram from './ChordDiagram';
import './SongList.css'; 

const API_URL = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api/chords` 
  : 'http://localhost:8000/api/chords';

const ChordLibrary = ({ onBack, token, userPlan }) => {
  const [chordMap, setChordMap] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [formError, setFormError] = useState("");
  const [loadError, setLoadError] = useState("");
  const canAddChords = userPlan === 'original';
  
  // Form State
  const [newChordName, setNewChordName] = useState("");
  const [newFrets, setNewFrets] = useState([-1, -1, -1, -1, -1, -1]); 
  const [newBarre, setNewBarre] = useState("");

  useEffect(() => {
    fetch(API_URL)
      .then(async res => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || "Could not load chord library.");
        }
        return res.json();
      })
      .then(data => {
        setChordMap(data);
        setLoadError("");
      })
      .catch(err => {
        console.error(err);
        setLoadError(err.message || "Could not load chord library.");
      });
  }, []);

  const handleAddChord = async () => {
    setFormError("");
    if (!canAddChords) {
      setFormError("Your plan does not include chord creation.");
      return;
    }
    if (!newChordName) {
      setFormError("Please enter a chord name.");
      return;
    }
    
    const newVariation = {
      frets: newFrets.map(f => (f === "" ? -1 : parseInt(f))),
      fingers: [0, 0, 0, 0, 0, 0], 
      barres: newBarre ? [parseInt(newBarre)] : [], 
      capo: false
    };

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ name: newChordName, variation: newVariation })
      });

      if (res.ok) {
        const updatedMap = await res.json();
        setChordMap(updatedMap);
        setIsAdding(false);
        setNewChordName("");
        setNewFrets([-1,-1,-1,-1,-1,-1]);
        setNewBarre("");
      } else {
        const data = await res.json().catch(() => ({}));
        setFormError(data.message || "Your plan does not include this action.");
      }
    } catch (err) {
      setFormError("Connection error. Please try again.");
      console.error(err);
    }
  };

  const updateFret = (index, value) => {
    let val = parseInt(value);
    if (isNaN(val)) val = "";
    const copy = [...newFrets];
    copy[index] = val;
    setNewFrets(copy);
  };

  return (
    <div className="home-container">
      <div className="home-header">
        <button className="back-btn" onClick={onBack}>← Back</button>
        <div className="chord-library-title">
          <h1>Chord Library</h1>
          {canAddChords ? (
            <span className="badge-original">Original Member: chord additions enabled</span>
          ) : (
            <span className="limit-note">Only Original Members can add chords</span>
          )}
        </div>
        <button
          className="btn-create-large"
          onClick={() => {
            if (canAddChords) setIsAdding(true);
          }}
          disabled={!canAddChords}
          title={canAddChords ? 'Add a new chord variation' : 'Only Original Members can add chords.'}
        >
          {canAddChords ? '+ Add New Chord' : 'Add Chord Locked'}
        </button>
      </div>

      {loadError && <div className="form-error">Error: {loadError}</div>}

      {/* --- ADD CHORD FORM --- */}
      {isAdding && canAddChords && (
        <div className="chord-form-panel">
          <h3>Add Chord Variation</h3>
          {formError && <div className="form-error">Error: {formError}</div>}
          
          <div className="form-row">
            <div className="form-group" style={{flex: 1}}>
                <label className="tool-label">Chord Name</label>
                <input 
                    placeholder="e.g. Bm" 
                    value={newChordName}
                    onChange={e => setNewChordName(e.target.value)}
                    className="tool-input"
                />
            </div>
            <div className="form-group" style={{width: '120px'}}>
                <label className="tool-label">Barre Fret</label>
                <input 
                    type="number"
                    placeholder="Ex: 2" 
                    value={newBarre}
                    onChange={e => setNewBarre(e.target.value)}
                    className="tool-input"
                    style={{textAlign:'center'}}
                />
            </div>
          </div>
          
          <div className="string-labels">
             <span>Low E (6)</span>
             <span>High E (1)</span>
          </div>

          {/* THE GRID (This fixes the overflow) */}
          <div className="fret-grid">
            {newFrets.map((fret, i) => (
              <div key={i} className="fret-col">
                <input
                    type="number"
                    min="-1"
                    value={fret}
                    onChange={(e) => updateFret(i, e.target.value)}
                    className="fret-input"
                />
              </div>
            ))}
          </div>

          <div className="form-hint">
             Enter <b>-1</b> for Mute (X), <b>0</b> for Open (O), or the <b>Fret Number</b>.
          </div>

          <div className="btn-row">
             <button className="btn btn-danger" onClick={() => setIsAdding(false)}>Cancel</button>
             <button className="btn btn-success" onClick={handleAddChord}>Save Chord</button>
          </div>
        </div>
      )}

      {/* --- GRID OF CHORDS --- */}
      <div className="song-grid">
        {Object.keys(chordMap).sort().map(name => (
          <div key={name} className="song-card" style={{alignItems: 'center', padding: '15px'}}>
            <h3 style={{margin: '0 0 10px 0'}}>{name}</h3>
            
            <div style={{transform: 'scale(0.85)'}}>
              <ChordDiagram chordData={chordMap[name][0]} name="" />
            </div>

            <p style={{color: '#888', fontSize: '0.8rem', marginTop: '10px'}}>
              {chordMap[name].length} variation{chordMap[name].length > 1 ? 's' : ''}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChordLibrary;
